import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import React, { useState, useCallback } from "react";
import { parseMarkdown } from "../parsers/markdown";
import { PieChart, GaugeChart, BarChart } from "./charts";
export function JsonRenderView({ document, className = "", style, onPathChange, onTransaction, onFormSubmit, onInclude, }) {
    return (_jsxs("div", { className: `soroban-render-json ${className}`, style: style, children: [document.title && _jsx("title", { children: document.title }), document.components.map((component, index) => (_jsx(ComponentRenderer, { component: component, onPathChange: onPathChange, onTransaction: onTransaction, onFormSubmit: onFormSubmit, onInclude: onInclude }, index)))] }));
}
function ComponentRenderer({ component, onPathChange, onTransaction, onFormSubmit, onInclude, }) {
    switch (component.type) {
        case "heading":
            return _jsx(HeadingRenderer, { component: component });
        case "text":
            return _jsx(TextRenderer, { component: component });
        case "markdown":
            return _jsx(MarkdownRenderer, { component: component });
        case "divider":
            return _jsx(DividerRenderer, {});
        case "form":
            return _jsx(FormRenderer, { component: component, onSubmit: onFormSubmit });
        case "button":
            return (_jsx(ButtonRenderer, { component: component, onPathChange: onPathChange, onTransaction: onTransaction }));
        case "list":
            return (_jsx(ListRenderer, { component: component, onPathChange: onPathChange, onTransaction: onTransaction, onFormSubmit: onFormSubmit, onInclude: onInclude }));
        case "task":
            return (_jsx(TaskRenderer, { component: component, onPathChange: onPathChange, onTransaction: onTransaction }));
        case "navigation":
            return _jsx(NavigationRenderer, { component: component, onPathChange: onPathChange });
        case "container":
            return (_jsx(ContainerRenderer, { component: component, onPathChange: onPathChange, onTransaction: onTransaction, onFormSubmit: onFormSubmit, onInclude: onInclude }));
        case "include":
            if (onInclude) {
                return _jsx(_Fragment, { children: onInclude(component.contract, component.path) });
            }
            return (_jsxs("div", { className: "soroban-render-include-placeholder", children: ["[Include: ", component.contract, "]"] }));
        case "chart":
            return _jsx(ChartRenderer, { component: component });
        default:
            return null;
    }
}
function HeadingRenderer({ component }) {
    const level = Math.min(Math.max(component.level, 1), 6);
    switch (level) {
        case 1: return _jsx("h1", { children: component.text });
        case 2: return _jsx("h2", { children: component.text });
        case 3: return _jsx("h3", { children: component.text });
        case 4: return _jsx("h4", { children: component.text });
        case 5: return _jsx("h5", { children: component.text });
        default: return _jsx("h6", { children: component.text });
    }
}
function TextRenderer({ component }) {
    return _jsx("p", { children: component.content });
}
function MarkdownRenderer({ component }) {
    const [html, setHtml] = useState("");
    React.useEffect(() => {
        parseMarkdown(component.content).then(setHtml);
    }, [component.content]);
    return _jsx("div", { dangerouslySetInnerHTML: { __html: html } });
}
function DividerRenderer() {
    return _jsx("hr", {});
}
function FormRenderer({ component, onSubmit }) {
    const getInitialFormData = useCallback(() => {
        const initial = {};
        component.fields.forEach((field) => {
            if (field.type === "checkbox") {
                initial[field.name] = field.checked || false;
            }
            else {
                initial[field.name] = field.value || "";
            }
        });
        return initial;
    }, [component.fields]);
    const [formData, setFormData] = useState(getInitialFormData);
    const handleChange = useCallback((name, value) => {
        setFormData((prev) => ({ ...prev, [name]: value }));
    }, []);
    const handleSubmit = useCallback((e) => {
        e.preventDefault();
        if (onSubmit) {
            onSubmit(component.action, formData);
            // Clear form after submission
            setFormData(getInitialFormData());
        }
    }, [component.action, formData, onSubmit, getInitialFormData]);
    return (_jsxs("form", { className: "soroban-render-form", onSubmit: handleSubmit, children: [component.fields.map((field, index) => (_jsx(FieldRenderer, { field: field, value: formData[field.name], onChange: (value) => handleChange(field.name, value) }, index))), _jsx("button", { type: "submit", className: "soroban-render-button soroban-render-button-primary", children: component.submitLabel || "Submit" })] }));
}
function FieldRenderer({ field, value, onChange }) {
    const id = `field-${field.name}`;
    if (field.type === "textarea") {
        return (_jsxs("div", { className: "soroban-render-field", children: [field.label && _jsx("label", { htmlFor: id, children: field.label }), _jsx("textarea", { id: id, name: field.name, placeholder: field.placeholder, required: field.required, value: String(value || ""), onChange: (e) => onChange(e.target.value) })] }));
    }
    if (field.type === "select") {
        return (_jsxs("div", { className: "soroban-render-field", children: [field.label && _jsx("label", { htmlFor: id, children: field.label }), _jsxs("select", { id: id, name: field.name, required: field.required, value: String(value || ""), onChange: (e) => onChange(e.target.value), children: [_jsx("option", { value: "", children: "Select..." }), field.options?.map((option, index) => (_jsx("option", { value: option.value, children: option.label }, index)))] })] }));
    }
    if (field.type === "checkbox") {
        return (_jsxs("div", { className: "soroban-render-field soroban-render-field-checkbox", children: [_jsx("input", { id: id, type: "checkbox", name: field.name, checked: Boolean(value), onChange: (e) => onChange(e.target.checked) }), _jsx("label", { htmlFor: id, children: field.label || field.name })] }));
    }
    return (_jsxs("div", { className: "soroban-render-field", children: [field.label && _jsx("label", { htmlFor: id, children: field.label }), _jsx("input", { id: id, type: field.type, name: field.name, placeholder: field.placeholder, required: field.required, value: String(value || ""), onChange: (e) => onChange(e.target.value) })] }));
}
function ButtonRenderer({ component, onPathChange, onTransaction, }) {
    const handleClick = useCallback(() => {
        if (component.action === "render" && component.path && onPathChange) {
            onPathChange(component.path);
        }
        else if (component.action === "tx" && component.method && onTransaction) {
            onTransaction(component.method, component.args || {});
        }
    }, [component, onPathChange, onTransaction]);
    const variantClass = component.variant
        ? `soroban-render-button-${component.variant}`
        : "soroban-render-button-secondary";
    return (_jsx("button", { type: "button", className: `soroban-render-button ${variantClass}`, onClick: handleClick, children: component.label }));
}
function ListRenderer({ component, onPathChange, onTransaction, onFormSubmit, onInclude, }) {
    const Tag = component.ordered ? "ol" : "ul";
    return (_jsx(Tag, { className: "soroban-render-list", children: component.items.map((item, index) => {
            if ("type" in item && item.type === "item") {
                // Simple list item
                if (typeof item.content === "string") {
                    return _jsx("li", { children: item.content }, index);
                }
                return (_jsx("li", { children: _jsx(ComponentRenderer, { component: item.content, onPathChange: onPathChange, onTransaction: onTransaction, onFormSubmit: onFormSubmit, onInclude: onInclude }) }, index));
            }
            // Direct component in list
            return (_jsx("li", { children: _jsx(ComponentRenderer, { component: item, onPathChange: onPathChange, onTransaction: onTransaction, onFormSubmit: onFormSubmit, onInclude: onInclude }) }, index));
        }) }));
}
function TaskRenderer({ component, onPathChange, onTransaction, }) {
    const handleAction = useCallback((action) => {
        if (action.type === "render" && action.path && onPathChange) {
            onPathChange(action.path);
        }
        else if (action.type === "tx" && action.method && onTransaction) {
            onTransaction(action.method, action.args || {});
        }
    }, [onPathChange, onTransaction]);
    return (_jsxs("div", { className: `soroban-render-task ${component.completed ? "completed" : ""}`, children: [_jsx("span", { className: "soroban-render-task-checkbox", children: component.completed ? "☑" : "☐" }), _jsx("span", { className: `soroban-render-task-text ${component.completed ? "line-through" : ""}`, children: component.text }), component.actions && component.actions.length > 0 && (_jsx("span", { className: "soroban-render-task-actions", children: component.actions.map((action, index) => (_jsx("button", { type: "button", className: "soroban-render-task-action", onClick: () => handleAction(action), children: action.label }, index))) }))] }));
}
function NavigationRenderer({ component, onPathChange, }) {
    return (_jsx("nav", { className: "soroban-render-navigation", children: component.items.map((item, index) => (_jsxs(React.Fragment, { children: [index > 0 && _jsx("span", { className: "soroban-render-nav-separator", children: "|" }), _jsx("a", { href: "#", className: `soroban-render-nav-link ${item.active ? "active" : ""}`, onClick: (e) => {
                        e.preventDefault();
                        if (onPathChange) {
                            onPathChange(item.path);
                        }
                    }, children: item.label })] }, index))) }));
}
function ContainerRenderer({ component, onPathChange, onTransaction, onFormSubmit, onInclude, }) {
    return (_jsx("div", { className: `soroban-render-container ${component.className || ""}`, children: component.components.map((child, index) => (_jsx(ComponentRenderer, { component: child, onPathChange: onPathChange, onTransaction: onTransaction, onFormSubmit: onFormSubmit, onInclude: onInclude }, index))) }));
}
function ChartRenderer({ component }) {
    switch (component.chartType) {
        case "pie":
            return _jsx(PieChart, { data: component.data, title: component.title });
        case "gauge":
            return (_jsx(GaugeChart, { value: component.value, max: component.max, label: component.label, color: component.color }));
        case "bar":
            return _jsx(BarChart, { data: component.data, title: component.title });
        default:
            return _jsx("div", { children: "Unknown chart type" });
    }
}
export const jsonStyles = `
.soroban-render-json {
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Oxygen, Ubuntu, sans-serif;
  line-height: 1.6;
  color: #333;
}

.soroban-render-form {
  margin: 1rem 0;
}

.soroban-render-field {
  margin-bottom: 0.75rem;
}

.soroban-render-field label {
  display: block;
  margin-bottom: 0.25rem;
  font-weight: 500;
}

.soroban-render-field input,
.soroban-render-field textarea,
.soroban-render-field select {
  width: 100%;
  padding: 0.5rem;
  border: 1px solid #ccc;
  border-radius: 4px;
  font-size: 1rem;
}

.soroban-render-field input:focus,
.soroban-render-field textarea:focus,
.soroban-render-field select:focus {
  outline: none;
  border-color: #0066cc;
  box-shadow: 0 0 0 2px rgba(0, 102, 204, 0.2);
}

.soroban-render-field-checkbox {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.soroban-render-field-checkbox input {
  width: auto;
}

.soroban-render-field-checkbox label {
  display: inline;
  margin-bottom: 0;
  font-weight: normal;
}

.soroban-render-button {
  padding: 0.5rem 1rem;
  border: none;
  border-radius: 4px;
  font-size: 1rem;
  cursor: pointer;
  transition: background-color 0.2s;
}

.soroban-render-button-primary {
  background-color: #0066cc;
  color: white;
}

.soroban-render-button-primary:hover {
  background-color: #0052a3;
}

.soroban-render-button-secondary {
  background-color: #e0e0e0;
  color: #333;
}

.soroban-render-button-secondary:hover {
  background-color: #d0d0d0;
}

.soroban-render-button-danger {
  background-color: #dc3545;
  color: white;
}

.soroban-render-button-danger:hover {
  background-color: #c82333;
}

.soroban-render-list {
  margin: 0.5rem 0;
  padding-left: 1.5rem;
}

.soroban-render-task {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.25rem 0;
}

.soroban-render-task.completed .soroban-render-task-text {
  text-decoration: line-through;
  color: #666;
}

.soroban-render-task-checkbox {
  font-size: 1.1rem;
}

.soroban-render-task-text {
  flex: 1;
}

.soroban-render-task-actions {
  display: flex;
  gap: 0.25rem;
}

.soroban-render-task-action {
  padding: 0.125rem 0.5rem;
  border: 1px solid #ccc;
  border-radius: 3px;
  background: #f5f5f5;
  cursor: pointer;
  font-size: 0.875rem;
}

.soroban-render-task-action:hover {
  background: #e0e0e0;
}

.soroban-render-navigation {
  display: flex;
  gap: 0.5rem;
  align-items: center;
  margin: 0.5rem 0;
}

.soroban-render-nav-separator {
  color: #999;
}

.soroban-render-nav-link {
  color: #0066cc;
  text-decoration: none;
}

.soroban-render-nav-link:hover {
  text-decoration: underline;
}

.soroban-render-nav-link.active {
  font-weight: bold;
}

.soroban-render-container {
  margin: 0.5rem 0;
}

.soroban-render-include-placeholder {
  padding: 0.5rem;
  background: #f5f5f5;
  border: 1px dashed #ccc;
  border-radius: 4px;
  color: #666;
  font-style: italic;
}

.soroban-chart {
  margin: 1rem 0;
  padding: 1rem;
  background: #fafafa;
  border-radius: 8px;
}
`;
//# sourceMappingURL=JsonRenderView.js.map